/**
 * Gemini Voice Client — Real-time bidirectional audio streaming
 * Captures mic at 16kHz PCM, streams to backend, plays back 24kHz PCM from Gemini
 */

const SAMPLE_RATE_IN = 16000;   // Gemini expects 16kHz input
const SAMPLE_RATE_OUT = 24000;  // Gemini outputs 24kHz
const CHUNK_SIZE = 4096;        // PCM bytes per chunk

export class GeminiVoiceClient {
  constructor({ onTranscript, onUserTranscript, onError, onStateChange, onConnected }) {
    this.onTranscript = onTranscript;
    this.onUserTranscript = onUserTranscript;
    this.onError = onError;
    this.onStateChange = onStateChange;
    this.onConnected = onConnected;
    
    this.ws = null;
    this.audioContext = null;
    this.micStream = null;
    this.workletNode = null;
    this.isStreaming = false;
    this.playbackQueue = [];
    this.isPlaying = false;
  }

  async connect(sessionId) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = backendUrl.replace(/^http/, 'ws');
    const url = `${wsBase}/api/voice/${sessionId}`;
    
    console.log('[GEMINI] Connecting:', url);
    
    this.ws = new WebSocket(url);
    
    this.ws.onopen = () => {
      console.log('[GEMINI] WebSocket connected');
    };
    
    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        
        if (msg.type === 'connected') {
          console.log('[GEMINI] Session connected, Gemini:', msg.gemini);
          if (this.onConnected) this.onConnected(msg.gemini);
        }
        
        if (msg.type === 'audio') {
          // Decode base64 PCM audio and queue for playback
          const audioBytes = this._b64ToArrayBuffer(msg.data);
          this._queuePlayback(audioBytes);
        }
        
        if (msg.type === 'jarvis_transcript') {
          if (this.onTranscript) this.onTranscript(msg.text);
        }
        
        if (msg.type === 'user_transcript') {
          if (this.onUserTranscript) this.onUserTranscript(msg.text);
        }
        
        if (msg.type === 'error') {
          if (this.onError) this.onError(msg.message);
        }
      } catch (e) {
        console.error('[GEMINI] Message parse error:', e);
      }
    };
    
    this.ws.onclose = () => {
      console.log('[GEMINI] WebSocket closed');
      this.isStreaming = false;
      if (this.onStateChange) this.onStateChange('disconnected');
    };
    
    this.ws.onerror = (err) => {
      console.error('[GEMINI] WebSocket error');
      if (this.onError) this.onError('Voice connection error');
    };
  }

  async startMic() {
    try {
      // Get mic stream
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE_IN,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      
      // Create audio context at 16kHz for Gemini
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE_IN
      });
      
      const source = this.audioContext.createMediaStreamSource(this.micStream);
      
      // Use ScriptProcessor (widely supported) to capture PCM
      const processor = this.audioContext.createScriptProcessor(2048, 1, 1);
      
      processor.onaudioprocess = (e) => {
        if (!this.isStreaming || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        
        const inputData = e.inputBuffer.getChannelData(0);
        // Convert float32 to int16 PCM
        const pcm = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          pcm[i] = Math.max(-32768, Math.min(32767, Math.floor(inputData[i] * 32768)));
        }
        
        // Send as base64 JSON (more compatible than binary)
        const b64 = this._arrayBufferToB64(pcm.buffer);
        this.ws.send(JSON.stringify({
          type: 'audio',
          data: b64
        }));
      };
      
      source.connect(processor);
      processor.connect(this.audioContext.destination);
      
      this.workletNode = processor;
      this.isStreaming = true;
      
      console.log('[GEMINI] Mic streaming started at', SAMPLE_RATE_IN, 'Hz');
      if (this.onStateChange) this.onStateChange('listening');
      
      return true;
    } catch (e) {
      console.error('[GEMINI] Mic error:', e);
      if (this.onError) this.onError('Microphone access denied');
      return false;
    }
  }

  stopMic() {
    this.isStreaming = false;
    if (this.micStream) {
      this.micStream.getTracks().forEach(t => t.stop());
      this.micStream = null;
    }
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.onStateChange) this.onStateChange('idle');
  }

  sendText(text) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'text', text }));
    }
  }

  // ── Audio Playback (24kHz PCM from Gemini) ──
  _queuePlayback(pcmArrayBuffer) {
    this.playbackQueue.push(pcmArrayBuffer);
    if (!this.isPlaying) {
      this._processPlaybackQueue();
    }
  }

  async _processPlaybackQueue() {
    if (this.playbackQueue.length === 0) {
      this.isPlaying = false;
      return;
    }
    
    this.isPlaying = true;
    
    // Create playback context at 24kHz
    const playCtx = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: SAMPLE_RATE_OUT
    });
    
    while (this.playbackQueue.length > 0) {
      const pcmBytes = this.playbackQueue.shift();
      const int16 = new Int16Array(pcmBytes);
      
      // Convert int16 to float32
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) {
        float32[i] = int16[i] / 32768.0;
      }
      
      // Create audio buffer and play
      const buffer = playCtx.createBuffer(1, float32.length, SAMPLE_RATE_OUT);
      buffer.getChannelData(0).set(float32);
      
      const source = playCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(playCtx.destination);
      
      // Play and wait for it to finish
      await new Promise(resolve => {
        source.onended = resolve;
        source.start();
      });
    }
    
    playCtx.close();
    this.isPlaying = false;
  }

  // ── Utility ──
  _b64ToArrayBuffer(b64) {
    const binaryString = atob(b64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  _arrayBufferToB64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  disconnect() {
    this.stopMic();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}
